export type ShortcutId = keyof typeof shortcuts
export type Bind = (typeof shortcuts)[ShortcutId]

type Shortcut = {
  /** id and key need to be the same,
   * TS will bark at you if you will not do that
   **/
  readonly id: string
  /** Key combination e.g. 'ctrl+c', 'alt+shift+s'. Case insensitive.
   * Always use 'ctrl' modifier (maps to Cmd on macOS automatically)
   */
  readonly shortcut: string
  /** Description for help text, starting with lowercase verb.
   * Will be formatted as: "Press [shortcut] to {desc}"
   * Example: desc="copy selected nodes" → "Press [ctrl+c] to copy selected nodes"
   */
  readonly desc: string
}

// ensure type safety helper fn, `key` need to match `id`
const defineShortcuts = <Shortcuts extends Record<string, Shortcut>>(shortcuts: {
  [KeyID in keyof Shortcuts]: Shortcuts[KeyID] & { id: KeyID } // override id string with KeyID
}) => shortcuts

export const shortcuts = defineShortcuts({
  "zoom-in": { id: "zoom-in", shortcut: "+", desc: "zoom in" },
  "zoom-in-secondary": { id: "zoom-in-secondary", shortcut: "=", desc: "zoom in" },
  "zoom-out": { id: "zoom-out", shortcut: "-", desc: "zoom out" },
  "fit-view": { id: "fit-view", shortcut: "f", desc: "fit view" },
  "toggle-lock": { id: "toggle-lock", shortcut: "l", desc: "toggle graph lock" },
  "pan-graph": { id: "pan-graph", shortcut: "space", desc: "pan graph" },
  "toggle-select-inside-group": {
    id: "toggle-select-inside-group",
    shortcut: "shift",
    desc: "select inside group (with the mouse)",
  },

  // Search & Info
  "toggle-search": { id: "toggle-search", shortcut: "shift+f", desc: "toggle search dialog" },
  "view-shortcuts": { id: "view-shortcuts", shortcut: "i", desc: "view active shortcuts" },
  "toggle-errors-drawer": {
    id: "toggle-errors-drawer",
    shortcut: "e",
    desc: "toggle errors drawer",
  },

  // Actions
  "delete-graph": { id: "delete-graph", shortcut: "shift+d", desc: "delete graph" },
  "share-graph": {
    id: "share-graph",
    shortcut: "shift+e",
    desc: "share graph  (with other builders, only for private graphs)",
  },
  "sample-graph": { id: "sample-graph", shortcut: "shift+s", desc: "sample graph" },
  "publish-graph": { id: "publish-graph", shortcut: "shift+p", desc: "publish graph" },
  "edit-dashboard": { id: "edit-dashboard", shortcut: "shift+c", desc: "edit dashboard" },
  "edit-metadata": { id: "edit-metadata", shortcut: "shift+m", desc: "edit metadata" },
  "edit-post-sampling-validation": {
    id: "edit-post-sampling-validation",
    shortcut: "shift+v",
    desc: "edit post sampling validation",
  },
  "expand-all-groups": { id: "expand-all-groups", shortcut: "shift+g", desc: "expand all groups" },
  "bulk-nodes-edit": {
    id: "bulk-nodes-edit",
    shortcut: "shift+r",
    desc: "bulk edit nodes",
  },
  "save-snapshot": { id: "save-snapshot", shortcut: "shift+q", desc: "save snapshot" },
  "restore-snapshot": { id: "restore-snapshot", shortcut: "shift+w", desc: "restore snapshot" },
  "copy-nodes": { id: "copy-nodes", shortcut: "ctrl+c", desc: "copy nodes to the clipboard" },
  "paste-nodes": { id: "paste-nodes", shortcut: "ctrl+v", desc: "paste nodes from the clipboard" },
  "save-form": { id: "save-form", shortcut: "ctrl+s", desc: "save form and close drawer" },
  "create-edges": { id: "create-edges", shortcut: "ctrl+e", desc: "create edges" },

  // Menus
  "close-node-menu": { id: "close-node-menu", shortcut: "esc", desc: "close the menu" },
  "close-edge-menu": { id: "close-edge-menu", shortcut: "esc", desc: "close the menu" },
  "close-pane-menu": { id: "close-pane-menu", shortcut: "esc", desc: "close the menu" },
  "close-drawer": { id: "close-drawer", shortcut: "esc", desc: "close drawer" },
})
